import React, { useState } from 'react';
import { X, AlertTriangle, CheckCircle, User, Calendar, Clock, MapPin } from 'lucide-react';

const ViolationResponseModal = ({ violation, onSubmit, onClose, isSubmitting }) => {
  const [selectedStatus, setSelectedStatus] = useState('');
  const [error, setError] = useState('');

  const statusOptions = [
    {
      status: 'pending',
      label: 'Pending Review',
      description: 'Reset to pending. This violation still needs to be reviewed.',
      icon: Clock,
      color: 'text-yellow-600',
      bgColor: 'bg-yellow-50',
      borderColor: 'border-yellow-300',
      selectedBorderColor: 'border-yellow-500'
    },
    {
      status: 'valid',
      label: 'Valid Violation',
      description: 'This is a legitimate violation. The employee did not comply with meal break requirements.',
      icon: AlertTriangle,
      color: 'text-red-600',
      bgColor: 'bg-red-50',
      borderColor: 'border-red-300',
      selectedBorderColor: 'border-red-500'
    },
    {
      status: 'corrected',
      label: 'Corrected',
      description: 'This violation has been corrected in the timekeeping system, or it was a false positive.',
      icon: CheckCircle,
      color: 'text-green-600',
      bgColor: 'bg-green-50',
      borderColor: 'border-green-300',
      selectedBorderColor: 'border-green-500'
    }
  ].filter(option => option.status !== violation.status);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!selectedStatus) {
      setError('Please select a status');
      return;
    }

    try {
      await onSubmit({
        violationId: violation.id,
        status: selectedStatus
      });
    } catch (err) {
      setError(err.message || 'Failed to update violation');
    }
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString + 'T12:00:00');
    return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
  };

  // Format time
  const formatTime = (timeString) => {
    if (!timeString) return '';
    const [hours, minutes] = timeString.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const hour12 = hour % 12 || 12;
    return `${hour12}:${minutes} ${ampm}`;
  };

  // Format hours worked
  const formatHours = (hours) => {
    if (!hours) return '';
    const h = Math.floor(hours);
    const m = Math.round((hours - h) * 60);
    return m > 0 ? `${h}h ${m}m` : `${h}h`;
  };

  // Violation type display names
  const violationTypeLabels = {
    meal_break_missing: 'Missing Meal Break',
    meal_break_short: 'Short Meal Break',
    meal_break_late: 'Late Meal Break',
    second_meal_missing: 'Missing 2nd Meal Break',
    second_meal_short: 'Short 2nd Meal Break',
    second_meal_late: 'Late 2nd Meal Break',
    paid_break_too_long: 'Paid Break Too Long'
  };

  const entry = violation.entry || violation.timeClockEntry;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold text-gray-900">Update Violation Status</h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors p-1"
            >
              <X className="h-6 w-6" />
            </button>
          </div>

          {/* Violation Summary */}
          <div className="bg-gray-50 rounded-lg p-4 mb-6">
            <h3 className="font-semibold text-gray-900 text-lg mb-3">
              {violationTypeLabels[violation.violationType] || violation.violationType}
            </h3>

            <div className="space-y-2 text-sm">
              <div className="flex items-center text-gray-700">
                <User className="h-4 w-4 mr-2 text-gray-400" />
                <span className="font-medium">{violation.employeeName || entry?.employeeName || 'Unknown Employee'}</span>
              </div>

              {(violation.location?.locationName || entry?.locationName) && (
                <div className="flex items-center text-gray-600">
                  <MapPin className="h-4 w-4 mr-2 text-gray-400" />
                  <span>
                    {violation.location?.locationNumber && `${violation.location.locationNumber} - `}
                    {violation.location?.locationName || entry?.locationName}
                  </span>
                </div>
              )}

              <div className="flex items-center text-gray-600">
                <Calendar className="h-4 w-4 mr-2 text-gray-400" />
                <span>{formatDate(violation.workDate || entry?.workDate)}</span>
              </div>

              {entry && (
                <div className="flex items-center text-gray-600">
                  <Clock className="h-4 w-4 mr-2 text-gray-400" />
                  <span>
                    {formatTime(entry.clockInTime || entry.clockIn)} - {formatTime(entry.clockOutTime || entry.clockOut)}
                    {' '}({formatHours(violation.shiftHours || entry.hoursWorked || entry.totalHours)})
                  </span>
                </div>
              )}

              {violation.description && (
                <p className="mt-2 text-gray-700 bg-white p-2 rounded border border-gray-200">
                  {violation.description}
                </p>
              )}
            </div>
          </div>

          {/* Response Form */}
          <form onSubmit={handleSubmit}>
            {/* Status Selection */}
            <div className="mb-6">
              <label className="block text-sm font-semibold text-gray-700 mb-3">
                Select Status
              </label>
              <div className="space-y-3">
                {statusOptions.map((option) => {
                  const Icon = option.icon;
                  const isSelected = selectedStatus === option.status;
                  return (
                    <button
                      key={option.status}
                      type="button"
                      onClick={() => setSelectedStatus(option.status)}
                      className={`w-full p-4 rounded-lg border-2 text-left transition-all ${
                        isSelected
                          ? `${option.selectedBorderColor} ${option.bgColor} ring-2 ring-offset-1 ${option.status === 'valid' ? 'ring-red-200' : 'ring-green-200'}`
                          : `${option.borderColor} hover:${option.bgColor} bg-white`
                      }`}
                    >
                      <div className="flex items-start space-x-3">
                        <div className={`p-2 rounded-full ${isSelected ? option.bgColor : 'bg-gray-100'}`}>
                          <Icon className={`h-5 w-5 ${isSelected ? option.color : 'text-gray-400'}`} />
                        </div>
                        <div className="flex-1">
                          <p className={`font-semibold ${isSelected ? option.color : 'text-gray-900'}`}>
                            {option.label}
                          </p>
                          <p className="text-sm text-gray-500 mt-1">{option.description}</p>
                        </div>
                        {isSelected && (
                          <div className={`flex-shrink-0 ${option.color}`}>
                            <CheckCircle className="h-5 w-5" />
                          </div>
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Error Message */}
            {error && (
              <div className="mb-4 p-3 rounded-md bg-red-50 text-red-700 text-sm flex items-center">
                <AlertTriangle className="h-4 w-4 mr-2 flex-shrink-0" />
                {error}
              </div>
            )}

            {/* Buttons */}
            <div className="flex space-x-3">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 px-4 py-3 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSubmitting || !selectedStatus}
                className={`flex-1 px-4 py-3 border border-transparent text-sm font-semibold rounded-md shadow-sm text-white focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors ${
                  selectedStatus === 'valid'
                    ? 'bg-red-600 hover:bg-red-700 focus:ring-red-500'
                    : selectedStatus === 'corrected'
                    ? 'bg-green-600 hover:bg-green-700 focus:ring-green-500'
                    : selectedStatus === 'pending'
                    ? 'bg-yellow-600 hover:bg-yellow-700 focus:ring-yellow-500'
                    : 'bg-primary-600 hover:bg-primary-700 focus:ring-primary-500'
                }`}
              >
                {isSubmitting ? 'Updating...' : selectedStatus === 'valid' ? 'Mark as Valid' : selectedStatus === 'corrected' ? 'Mark as Corrected' : selectedStatus === 'pending' ? 'Reset to Pending' : 'Update Status'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ViolationResponseModal;
